/**
 * Client store for temporary product-role demotion (“view as”).
 * Persists per browser tab in sessionStorage. Never elevates grants —
 * server re-checks authorization on every API call via X-View-As-Role.
 */

import {
  isProductRoleCode,
  type ProductRoleCode
} from '@/lib/product-roles';

export const VIEW_AS_ROLE_HEADER = 'X-View-As-Role';
const STORAGE_KEY = 'cdf.booking.viewAsRole';

type ViewAsListener = (role: ProductRoleCode | null) => void;

const listeners = new Set<ViewAsListener>();

function readStorage(): ProductRoleCode | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return isProductRoleCode(raw) ? raw : null;
  } catch {
    return null;
  }
}

function writeStorage(role: ProductRoleCode | null): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (!role) sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, role);
  } catch {
    // Storage may be unavailable in private modes — keep in-memory via listeners.
  }
}

let current: ProductRoleCode | null = readStorage();

export function getViewAsRole(): ProductRoleCode | null {
  return current;
}

export function setViewAsRole(role: ProductRoleCode | null): void {
  current = role;
  writeStorage(role);
  for (const listener of listeners) {
    listener(role);
  }
}

export function clearViewAsRole(): void {
  setViewAsRole(null);
}

/** Subscribe to view-as changes (returns unsubscribe). */
export function subscribeViewAsRole(listener: ViewAsListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
